'use client';

/**
 * BoardCanvas — React host for the "board" live system (TT-1046 Live
 * Tapestry v2): the graphic-recording board that grows through a series of
 * ScenePlan versions (platform `domain/scenegen/board.py`, archetype
 * "board"; each version the platform's plan read, `GET …/plans/{plan_id}?at=`).
 *
 * The board machinery is deliberately plain-DOM SVG (board/live.js solves,
 * board/renderLive.js mounts and animates); this component owns the React
 * seams only: font readiness, solving the wall when the version series
 * grows, mounting into an <svg>, and driving the clock.
 *
 * One mount per board (TT-1215): a re-solved wall for the same board — the
 * first version's title, frame and palette, which is what the mount seeds
 * from — is handed to the mounted renderer as an update, so what is on
 * the page stays as it is and only the new items reveal; the opening
 * sketch growing line by line is the same board too. A different board
 * (the sketch giving way to the planned one, a lab fixture swap) remounts.
 *
 * `t` is the reveal clock. Plans generated live stamp reveal times with
 * sentence ordinals (there is no per-sentence timing in the live stream),
 * so hosts pass the latest sentence idx — or Infinity to show everything
 * revealed so far. With Infinity the canvas paces each new batch itself
 * (TT-1162 note 7: a sequence the eye can follow, not a flood): on a
 * fresh mount everything before the newest version lands at once, and
 * from there the clock steps through the wall's stamps one at a time,
 * STEP_MS apart, a batch inside the scene's reveal budget.
 *
 * `instant` is a settled board — a closed meeting, a static preview — at
 * its end state with no intros. `onSentClick(sent)` makes every verbatim
 * line a link to its sentence (renderLive). The ref answers `exportPng()`,
 * the classic canvas's contract (lib/exportImage), and `drawingRect()`,
 * the client rect the panel is drawn in (lab annotations, TT-1220).
 */

import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { makeMeasure } from '../board/render.js';
import { pickAssets, useManifest, useTextBoxArt } from '../scene/sceneAssets.js';
import { stylesKey } from '../lib/artPick.js';
import { ensureFontReady } from '../lib/tapestryText';
import { exportTapestryPng } from '../lib/exportImage';
import { buildWall, wallEnd, wallStamps } from '../board/live.js';
import { mountLive } from '../board/renderLive.js';
import { REVEAL } from '../scene/reveal';

// Every face the board renderer draws with (board/render.js FONTS).
const FACES = ['Permanent Marker', 'Patrick Hand', 'Caveat', 'Kalam'];

// A live batch steps one stamp every STEP_MS, the whole batch inside the
// scene's reveal budget.
const STEP_MS = 700;

/** Resolve true once the board faces are loaded (measurement needs them). */
export function useBoardFonts() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    Promise.all(FACES.map((f) => ensureFontReady(f))).then(() => !cancelled && setReady(true));
    return () => {
      cancelled = true;
    };
  }, []);
  return ready;
}

/**
 * Solve the wall for a plan series. Re-solves only when the series grows (or
 * the series identity changes); the whole-series solve is the renderer's
 * stability contract — earlier versions' placements never move retroactively.
 *
 * `textBoxManifest`: the painted text boxes' manifest (useTextBoxArt).
 * Returns `[wall, end]`: null until fonts are ready or with no versions;
 * `end` is the last reveal stamp (scrubber max).
 */
export function useBoardWall(versions, textBoxManifest) {
  const fontsReady = useBoardFonts();
  const textBoxArt = useTextBoxArt(textBoxManifest); // the solve reserves each motif's reach from the art, so it waits for it too
  return useMemo(() => {
    if (!fontsReady || !textBoxArt || !versions?.length) return [null, 0];
    const wall = buildWall(versions, makeMeasure(), { textBoxArt });
    return [wall, wallEnd(wall)];
  }, [fontsReady, textBoxArt, versions]);
}

/**
 * The board's art (anchors and stickers): symbol_id → { url, stages, intro }
 * from its manifest (render.js; a URL, or the object a host loaded), or an
 * empty map when there is none; null until known.
 */
export function useBoardArt(manifest) {
  return useManifest(manifest, pickAssets);
}

export { useTextBoxArt }; // the hook lives with the manifest loader (scene/sceneAssets.js); hosts import it from here

/**
 * The client rect the board's panel is drawn in: its viewBox mapped through
 * the svg's screen CTM (the svg letterboxes it), or null before a mount.
 */
function drawingRect(svg) {
  const vb = svg?.viewBox?.baseVal;
  const m = svg?.getScreenCTM();
  if (!vb?.width || !m) return null;
  return { left: m.e + vb.x * m.a, top: m.f + vb.y * m.d, width: vb.width * m.a, height: vb.height * m.d };
}

/** What the mount seeds from the first version: the same board or not. */
const boardKey = (plan) => `${plan.title?.text ?? ''}|${plan.archetype}/${plan.metaphor}|${JSON.stringify(plan.palette || {})}`;

/**
 * `wall` (optional): a host that already ran useBoardWall — e.g. to size a
 * scrubber — passes its wall here so the series isn't solved twice.
 * `versions` stays required either way (the first version seeds palette and
 * title for the mount). `intro={false}` skips the anchor art's staged intro
 * (static previews). `artStyles` (style names) draws each symbol's art
 * piece tagged with one of them where the manifest has one (lib/artPick.js),
 * its default elsewhere; a change remounts the board. `textBoxManifest`:
 * the painted text boxes' (useTextBoxArt), for a board solved here.
 */
const BoardCanvas = forwardRef(function BoardCanvas(
  { versions, wall: wallProp, t = Infinity, artManifest, textBoxManifest, artStyles = null, intro = true, instant = false, onSentClick = null, meetingName, meetingDate, className, style },
  hostRef
) {
  const rootRef = useRef(null);
  const svgRef = useRef(null);
  // The mounted renderer, tagged with what it was mounted for.
  const liveRef = useRef(null);
  // The clock as last set on the mount (null: nothing shown yet).
  const clockRef = useRef(null);
  const [ownWall, ownEnd] = useBoardWall(wallProp ? null : versions, textBoxManifest);
  const wall = wallProp || ownWall;
  const end = wallProp ? wallEnd(wallProp) : ownEnd;
  const boardArt = useBoardArt(artManifest);
  // The styles by value: a host's fresh array each render is the same ask.
  const asked = stylesKey(artStyles);
  const styles = useMemo(() => (asked ? asked.split(',') : null), [asked]);
  // Frames whose anchor intro has played, kept across remounts (the sketch
  // → planned handoff), so the anchor doesn't rise again.
  const playedRef = useRef(new Set());
  // The click handler as last rendered: the mount binds one listener.
  const clickRef = useRef(onSentClick);
  useEffect(() => {
    clickRef.current = onSentClick;
  }, [onSentClick]);
  const linked = Boolean(onSentClick);

  useImperativeHandle(
    hostRef,
    () => ({
      exportPng: () => exportTapestryPng(rootRef.current, { meetingName, dateStr: meetingDate, tag: 'BoardCanvas' }),
      drawingRect: () => drawingRect(svgRef.current),
    }),
    [meetingName, meetingDate]
  );

  // Mount the board DOM, or hand the mount a re-solved wall of the same
  // board. Waits for the art manifest so the first mount draws the anchor
  // and can play its intro.
  const artKey = typeof artManifest === 'string' ? artManifest : 'host';
  const key = versions?.length ? `${boardKey(versions[0])}|${artKey}|${asked}|${intro}|${instant}|${linked}` : null;
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !wall || !boardArt || !key) return;
    if (liveRef.current?.key === key) {
      liveRef.current.update(wall);
      return;
    }
    svg.replaceChildren();
    liveRef.current = {
      key,
      ...mountLive(svg, wall, versions[0], {
        boardArt,
        artStyles: styles,
        intro,
        instant,
        played: playedRef.current,
        onSentClick: linked ? (sent) => clickRef.current?.(sent) : null,
      }),
    };
    clockRef.current = null;
  }, [wall, versions, boardArt, styles, key, intro, instant, linked]);

  // Drive the clock. A finite `t` is set at once (a scrubbing host). With
  // Infinity the canvas steps from where it last settled to the wall's end
  // one stamp at a time — on a fresh mount from just before the newest
  // version's first stamp, so history lands whole and only the newest
  // version paces; an `instant` board lands whole. Its deps are a superset
  // of the mount's, and effects run in declaration order, so every
  // (re)mount is followed by a tick.
  useEffect(() => {
    const live = liveRef.current;
    if (!live || !wall) return undefined;
    if (t !== Infinity || instant) {
      const at = Math.min(t, end);
      live.setTime(at);
      clockRef.current = at;
      return undefined;
    }
    const newest = wall.versionT.filter(Number.isFinite).pop();
    const from = clockRef.current ?? (newest == null ? end : newest - 1e-6);
    const stamps = wallStamps(wall, from, end);
    if (!stamps.length) {
      live.setTime(end);
      clockRef.current = end;
      return undefined;
    }
    // Everything before the batch lands now; the batch paces.
    const step = Math.min(STEP_MS, REVEAL.budget / stamps.length);
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      const at = i === stamps.length ? end : stamps[i - 1];
      live.setTime(at);
      clockRef.current = at;
      if (i === stamps.length) clearInterval(id);
    }, step);
    return () => clearInterval(id);
  }, [t, wall, end, versions, boardArt, styles, key, intro, instant, linked]);

  return (
    <div ref={rootRef} className={className} style={{ width: '100%', height: '100%', position: 'relative', ...style }}>
      <svg ref={svgRef} style={{ width: '100%', height: '100%', display: 'block' }} role="img" aria-label="Live meeting board" />
    </div>
  );
});

export default BoardCanvas;
